import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, 
  Edit, 
  Settings, 
  Trophy,
  BookOpen,
  Star,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  RefreshCw
} from 'lucide-react';
import { useRequireAuth } from '@/hooks/use-auth-hooks';
import { useProfileContext } from '@/contexts/profile-context';
import { UserProfile } from '@/types/profile';
import { ProfileStrengthCard } from '@/components/profile/profile-strength-card';
import { ProfileForm } from '@/components/profile/profile-form';
import { createProfileStrengthData } from '@/utils/profile-strength';
import { getUserDisplayName, getUserInitials } from '@/utils/user-profile';
import { getProfileCategories } from '@/config/profile-fields';
import { formatDateForDisplay } from '@/utils/date-helpers';
import { AuthUser } from '@/types/auth';

/**
 * Gets the best available avatar URL by checking both profile data and auth metadata
 */
function getUnifiedAvatarUrl(profile: UserProfile | null, user: AuthUser | null): string | undefined {
  // First priority: database profile avatar_url
  if (profile?.avatar_url) {
    return profile.avatar_url;
  }
  
  if (!user) {
    return undefined;
  }
  
  // Fallback to auth metadata (same logic as sidebar)
  if (user.user_metadata?.avatar_url) {
    return user.user_metadata.avatar_url;
  }
  if (user.user_metadata?.picture) {
    return user.user_metadata.picture;
  }
  if (user.user_metadata?.avatar) {
    return user.user_metadata.avatar;
  }
  if (user.user_metadata?.photo) {
    return user.user_metadata.photo;
  }
  
  // Check Google identity data
  const googleIdentity = user.identities?.find((id: any) => id.provider === 'google');
  if (googleIdentity?.identity_data) {
    try {
      const identityData = typeof googleIdentity.identity_data === 'string' 
        ? JSON.parse(googleIdentity.identity_data) 
        : googleIdentity.identity_data;
      
      if (identityData.picture) {
        return identityData.picture;
      }
      if (identityData.avatar_url) {
        return identityData.avatar_url;
      }
      if (identityData.photo) {
        return identityData.photo;
      }
      if (identityData.image) {
        return identityData.image;
      }
    } catch (error) {
      console.error('ProfilePage: Error parsing Google identity_data:', error);
    }
  }
  
  return undefined;
}

export function ProfilePage() {
  const { user } = useRequireAuth();
  const { profile: contextProfile, profileStrength, isLoading: contextLoading, refreshProfile, updateProfile } = useProfileContext();
  const location = useLocation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [initialFormTab, setInitialFormTab] = useState<string | undefined>();
  const [highlightField, setHighlightField] = useState<string | undefined>();
  
  // Add ref to track last processed profile ID to prevent redundant updates
  const lastProcessedProfileIdRef = useRef<string | null>(null);

  // Set page title
  useEffect(() => {
    document.title = 'Profile - AceHorizon';
  }, []);

  // Convert context profile to UserProfile format - with stable dependencies
  useEffect(() => {
    if (contextProfile && user) {
      // Check if we've already processed this exact profile to prevent redundant updates
      const profileKey = `${contextProfile.id}-${contextProfile.updated_at}`;
      if (lastProcessedProfileIdRef.current === profileKey) {
        return;
      }
      
      lastProcessedProfileIdRef.current = profileKey;
      
      const convertedProfile: UserProfile = {
        id: contextProfile.id,
        user_id: contextProfile.user_id,
        user_type: contextProfile.profile_type,
        email: user.email || '',
        first_name: contextProfile.first_name,
        last_name: contextProfile.last_name,
        full_name: `${contextProfile.first_name} ${contextProfile.last_name}`.trim(),
        phone: contextProfile.phone,
        date_of_birth: contextProfile.date_of_birth,
        avatar_url: contextProfile.avatar_url,
        bio: contextProfile.bio,
        location: contextProfile.location,
        student_details: contextProfile.student_details,
        teacher_details: contextProfile.teacher_details,
        profile_strength: contextProfile.profile_strength,
        last_updated: contextProfile.updated_at,
        created_at: contextProfile.created_at,
      };
      
      setProfile(convertedProfile);
      setIsLoading(false);
    } else if (!contextLoading && !contextProfile && user) {
      // Reset the ref if there's no profile
      lastProcessedProfileIdRef.current = null;
      setIsLoading(false);
    }
  }, [contextProfile?.id, contextProfile?.updated_at, contextLoading, user?.id]); // Include updated_at to catch updates

  const userType = profile?.user_type || 'student'; // Default fallback
  const strengthData = profile ? createProfileStrengthData(profile, userType) : null;

  useEffect(() => {
    // Handle navigation from dialog
    if (location.state?.editMode) {
      setIsEditing(true);
      
      if (location.state.initialTab) {
        setInitialFormTab(location.state.initialTab);
      }
      
      if (location.state.highlightField) {
        setHighlightField(location.state.highlightField);
      }
      
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle profile refresh
  const handleRefreshProfile = async () => {
    setIsRefreshing(true);
    try {
      await refreshProfile();
      // The profile state will be updated automatically through the context
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSaveProfile = async (updatedProfile: UserProfile) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    if (!updateProfile) {
      throw new Error('Update profile method not available');
    }

    try {
      // Update the profile using the context which calls the PUT API
      // The updateProfile method will handle updating the stored data with the PUT response
      await updateProfile(updatedProfile);
      
      // The profile context has already been updated with the PUT response data
      // No need to call refreshProfile() or manually update local state
      setIsEditing(false);
      
      // Clear navigation state
      setInitialFormTab(undefined);
      setHighlightField(undefined);
    } catch (error: any) {
      console.error('ProfilePage: Error saving profile:', error);
      throw error; // Re-throw to let the form handle the error
    }
  };

  const handleNavigateToField = (fieldKey: string, categoryKey: string) => {
    setInitialFormTab(categoryKey);
    setHighlightField(fieldKey);
    setIsEditing(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Profile Not Found</h3>
        <p className="text-muted-foreground">
          Unable to load your profile. Please try again later.
        </p>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="container max-w-6xl mx-auto p-6">
        <ProfileForm
          profile={profile}
          userType={userType}
          onSave={handleSaveProfile}
          onCancel={() => {
            setIsEditing(false);
            setInitialFormTab(undefined);
            setHighlightField(undefined);
          }}
          initialTab={initialFormTab}
          highlightField={highlightField}
        />
      </div>
    );
  }

  const getInitials = (firstName?: string, lastName?: string) => {
    if (user) {
      return getUserInitials(user);
    }
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || 'U';
  };

  const formatListItems = (items?: string[]) => {
    if (!items || items.length === 0) return 'Not specified';
    return items.join(', ');
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Field completion check that matches profile strength calculation
  const isFieldCompleted = (value: any, fieldType: string): boolean => {
    if (value === null || value === undefined) return false;
    
    switch (fieldType) {
      case 'text':
      case 'email':
      case 'tel':
      case 'textarea':
      case 'date':
      case 'select':
        return typeof value === 'string' && value.trim().length > 0;
      
      case 'number':
        return typeof value === 'number' && value >= 0;
      
      case 'multiselect':
        return Array.isArray(value) && value.length > 0;
      
      case 'avatar':
        return typeof value === 'string' && value.trim().length > 0;
      
      default:
        return false;
    }
  };

  // Enhanced learning preference display utilities
  const getLearningStyleInfo = (style?: string) => {
    const styles: Record<string, { name: string; description: string; icon: string }> = {
      'Visual (diagrams, charts)': {
        name: 'Visual Learner',
        description: 'You learn best through diagrams, charts, and visual aids',
        icon: '👁️'
      },
      'Auditory (listening, discussions)': {
        name: 'Auditory Learner', 
        description: 'You prefer listening to lectures and participating in discussions',
        icon: '👂'
      },
      'Kinesthetic (hands-on activities)': {
        name: 'Kinesthetic Learner',
        description: 'You learn best through hands-on activities and movement',
        icon: '✋'
      },
      'Reading/Writing (text-based)': {
        name: 'Reading/Writing Learner',
        description: 'You prefer reading and writing to absorb information',
        icon: '📚'
      }
    };
    return style ? styles[style] : null;
  };

  const getStudyTimeInfo = (schedule?: string) => {
    const schedules: Record<string, { name: string; description: string; icon: string }> = {
      'Early Morning (6-9 AM)': {
        name: 'Early Bird',
        description: 'You\'re most productive in the early morning hours',
        icon: '🌅'
      },
      'Morning (9-12 PM)': {
        name: 'Morning Person',
        description: 'You focus best during morning hours',
        icon: '☀️'
      },
      'Afternoon (12-4 PM)': {
        name: 'Afternoon Learner',
        description: 'You study effectively in the afternoon',
        icon: '🌤️'
      },
      'Evening (4-8 PM)': {
        name: 'Evening Studier',
        description: 'You prefer studying in the evening hours',
        icon: '🌆'
      },
      'Night (8-11 PM)': {
        name: 'Night Owl',
        description: 'You\'re most focused during nighttime study sessions',
        icon: '🌙'
      }
    };
    return schedule ? schedules[schedule] : null;
  };

  return (
    <div className="container max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent rounded-2xl p-8 border border-primary/10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <Avatar className="w-24 h-24 ring-4 ring-white shadow-lg dark:ring-gray-800">
                <AvatarImage src={getUnifiedAvatarUrl(profile, user)} alt={`${profile.first_name} ${profile.last_name}`} />
                <AvatarFallback className="text-2xl font-semibold bg-gradient-to-br from-primary to-primary/80 text-white">
                  {getInitials(profile.first_name, profile.last_name)}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-3 border-white dark:border-gray-800 flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
                {profile.first_name || profile.last_name 
                  ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                  : user ? getUserDisplayName(user) : 'User'
                }
              </h1>
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium">{profile.email}</p>
                {profile.phone && (
                  <p className="text-muted-foreground text-sm flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    {profile.phone}
                  </p>
                )}
                {/* Profile Metadata */}
                <div className="flex flex-col gap-1 text-xs text-muted-foreground/80 mt-2">
                  {profile.created_at && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      <span>Joined {new Date(profile.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                  )}
                  {profile.last_updated && (
                    <div className="flex items-center gap-2">
                      <Edit className="w-3 h-3" />
                      <span>Last updated {new Date(profile.last_updated).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3 mt-3">
                <Badge variant="default" className="capitalize font-medium px-3 py-1">
                  {profile.user_type}
                </Badge>
                {strengthData && (
                  <Badge 
                    variant="outline" 
                    className={`font-medium px-3 py-1 ${
                      strengthData.totalStrength >= 80 
                        ? 'border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-300 dark:bg-green-950/30' 
                        : strengthData.totalStrength >= 60
                        ? 'border-blue-200 text-blue-700 bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:bg-blue-950/30'
                        : 'border-orange-200 text-orange-700 bg-orange-50 dark:border-orange-800 dark:text-orange-300 dark:bg-orange-950/30'
                    }`}
                  >
                    {Math.floor(strengthData.totalStrength)}% Complete
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={handleRefreshProfile}
              variant="outline"
              size="lg"
              disabled={isRefreshing || contextLoading}
              className="shadow-md hover:shadow-lg transition-all duration-200"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button 
              onClick={() => setIsEditing(true)}
              size="lg"
              className="shadow-md hover:shadow-lg transition-all duration-200"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Strength Card */}
        <div className="lg:col-span-1">
          {strengthData && (
            <div className="sticky top-6">
              <ProfileStrengthCard
                strengthData={strengthData}
                userType={userType}
                onEditProfile={() => setIsEditing(true)}
              />
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 h-12 p-1 bg-muted/50">
              <TabsTrigger 
                value="overview" 
                className="font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="details" 
                className="font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
              >
                Details
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              {/* Basic Information */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <span>Contact Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                        <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-muted-foreground mb-1">Email Address</p>
                        <p className="text-sm text-foreground break-all">{profile.email}</p>
                      </div>
                    </div>
                    
                    {profile.phone && (
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950/30">
                          <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-muted-foreground mb-1">Phone Number</p>
                          <p className="text-sm text-foreground">{profile.phone}</p>
                        </div>
                      </div>
                    )}
                    
                    {profile.location && (
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-950/30">
                          <MapPin className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-muted-foreground mb-1">Location</p>
                          <p className="text-sm text-foreground">{profile.location}</p>
                        </div>
                      </div>
                    )}
                    
                    {profile.date_of_birth && (
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-950/30">
                          <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-muted-foreground mb-1">Date of Birth</p>
                          <p className="text-sm text-foreground">{formatDateForDisplay(profile.date_of_birth)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Bio */}
              {profile.bio && (
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <span>About</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="prose prose-sm max-w-none">
                      <p className="text-muted-foreground leading-relaxed">{profile.bio}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Student/Teacher Specific Info */}
              {profile.user_type === 'student' && profile.student_details && (
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <GraduationCap className="w-5 h-5 text-primary" />
                      </div>
                      <span>Academic Information</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-foreground">Class/Grade</p>
                        <p className="text-sm text-muted-foreground">{profile.student_details.class_grade || 'Not specified'}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-foreground">School</p>
                        <p className="text-sm text-muted-foreground">{profile.student_details.school_name || 'Not specified'}</p>
                      </div>
                    </div>
                    
                    {/* Learning Preferences Section */}
                    <div className="border-t border-border/50 pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Settings className="w-4 h-4 text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">Learning Preferences</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Learning Style */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-md bg-purple-50 dark:bg-purple-950/30">
                              <Star className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <p className="text-sm font-medium text-foreground">Learning Style</p>
                          </div>
                          {profile.student_details.learning_style ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{getLearningStyleInfo(profile.student_details.learning_style)?.icon}</span>
                                <Badge 
                                  variant="outline" 
                                  className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:border-purple-800 dark:text-purple-300 font-medium"
                                >
                                  {getLearningStyleInfo(profile.student_details.learning_style)?.name}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {getLearningStyleInfo(profile.student_details.learning_style)?.description}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground italic">No learning style selected</p>
                              <p className="text-xs text-muted-foreground">Add your preferred learning style to get personalized content</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Study Schedule */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-md bg-green-50 dark:bg-green-950/30">
                              <Calendar className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                            </div>
                            <p className="text-sm font-medium text-foreground">Preferred Study Time</p>
                          </div>
                          {profile.student_details.study_schedule ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{getStudyTimeInfo(profile.student_details.study_schedule)?.icon}</span>
                                <Badge 
                                  variant="outline" 
                                  className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-950/30 dark:border-green-800 dark:text-green-300 font-medium"
                                >
                                  {getStudyTimeInfo(profile.student_details.study_schedule)?.name}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {getStudyTimeInfo(profile.student_details.study_schedule)?.description}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground italic">No study time preference set</p>
                              <p className="text-xs text-muted-foreground">Set your optimal study hours for better scheduling</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">Academic Interests</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.student_details.academic_interests?.length ? (
                          profile.student_details.academic_interests.map(interest => (
                            <Badge key={interest} variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-300">
                              {interest}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">Not specified</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {profile.user_type === 'teacher' && profile.teacher_details && (
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg font-semibold">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Briefcase className="w-5 h-5 text-primary" />
                      </div>
                      <span>Professional Information</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-foreground">Teaching Experience</p>
                        <p className="text-sm text-muted-foreground">{profile.teacher_details.years_of_experience || 0} years</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-foreground">Institution</p>
                        <p className="text-sm text-muted-foreground">{profile.teacher_details.institution_name || 'Not specified'}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">Subjects Teaching</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.teacher_details.subjects_teaching?.length ? (
                          profile.teacher_details.subjects_teaching.map(subject => (
                            <Badge key={subject} variant="secondary" className="bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-300">
                              {subject}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">Not specified</span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">Qualifications</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.teacher_details.qualifications?.length ? (
                          profile.teacher_details.qualifications.map(qual => (
                            <Badge key={qual} variant="outline" className="border-orange-200 text-orange-700 hover:bg-orange-50 dark:border-orange-800 dark:text-orange-300">
                              {qual}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground">Not specified</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="details" className="space-y-8">
              {/* Detailed view with all categories */}
              {getProfileCategories(userType).map(category => {
                const categoryData = strengthData?.categories[category.key];
                const getCategoryIcon = (key: string) => {
                  switch (key) {
                    case 'basic_info': return <User className="w-5 h-5 text-primary" />;
                    case 'academic_info': 
                    case 'professional_info': return <BookOpen className="w-5 h-5 text-primary" />;
                    case 'preferences': 
                    case 'teaching_profile': return <Settings className="w-5 h-5 text-primary" />;
                    case 'personal_info': return <Star className="w-5 h-5 text-primary" />;
                    case 'additional_info': return <Trophy className="w-5 h-5 text-primary" />;
                    default: return <User className="w-5 h-5 text-primary" />;
                  }
                };
                
                return (
                  <Card key={category.key} className="border-0 shadow-sm">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            {getCategoryIcon(category.key)}
                          </div>
                          <div>
                            <CardTitle className="text-lg font-semibold">{category.title}</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
                          </div>
                        </div>
                        {categoryData && (
                          <Badge 
                            variant="outline" 
                            className={`${
                              categoryData.completed === categoryData.total 
                                ? 'border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-300 dark:bg-green-950/30' 
                                : 'border-orange-200 text-orange-700 bg-orange-50 dark:border-orange-800 dark:text-orange-300 dark:bg-orange-950/30'
                            }`}
                          >
                            {categoryData.completed}/{categoryData.total} completed
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {category.fields
                          .filter(field => field.key !== 'avatar_url') // Exclude avatar field since it's shown in header
                          .map(field => {
                          const value = getNestedValue(profile, field.key);
                          const hasValue = isFieldCompleted(value, field.type);
                          
                          // Special handling for specific field types
                          const isLearningStyle = field.key === 'student_details.learning_style';
                          const isStudySchedule = field.key === 'student_details.study_schedule';
                          const isAvatarField = field.key === 'avatar_url';
                          
                          // Skip avatar field since it's already displayed in the header
                          if (isAvatarField) {
                            return null;
                          }
                          
                          return (
                            <div key={field.key} className="space-y-3">
                              <div className="flex items-center gap-2">
                                {(isLearningStyle || isStudySchedule) && (
                                  <div className={`p-1.5 rounded-md ${
                                    isLearningStyle 
                                      ? 'bg-purple-50 dark:bg-purple-950/30' 
                                      : 'bg-green-50 dark:bg-green-950/30'
                                  }`}>
                                    {isLearningStyle ? (
                                      <Star className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                                    ) : (
                                      <Calendar className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                                    )}
                                  </div>
                                )}
                                <p className="text-sm font-semibold text-foreground">
                                  {field.label}
                                </p>
                              </div>
                              {hasValue ? (
                                <div>
                                  {Array.isArray(value) ? (
                                    <div className="flex flex-wrap gap-1.5">
                                      {value.map((item, index) => (
                                        <Badge 
                                          key={index} 
                                          variant="secondary" 
                                          className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-300"
                                        >
                                          {item}
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : isLearningStyle ? (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{getLearningStyleInfo(String(value))?.icon}</span>
                                        <Badge 
                                          variant="outline" 
                                          className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:border-purple-800 dark:text-purple-300 font-medium"
                                        >
                                          {getLearningStyleInfo(String(value))?.name}
                                        </Badge>
                                      </div>
                                      <p className="text-xs text-muted-foreground leading-relaxed">
                                        {getLearningStyleInfo(String(value))?.description}
                                      </p>
                                    </div>
                                  ) : isStudySchedule ? (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{getStudyTimeInfo(String(value))?.icon}</span>
                                        <Badge 
                                          variant="outline" 
                                          className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-950/30 dark:border-green-800 dark:text-green-300 font-medium"
                                        >
                                          {getStudyTimeInfo(String(value))?.name}
                                        </Badge>
                                      </div>
                                      <p className="text-xs text-muted-foreground leading-relaxed">
                                        {getStudyTimeInfo(String(value))?.description}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground leading-relaxed">{String(value)}</p>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <p className="text-sm text-muted-foreground italic">
                                    {isLearningStyle 
                                      ? 'No learning style selected' 
                                      : isStudySchedule 
                                      ? 'No study time preference set'
                                      : 'Not specified'
                                    }
                                  </p>
                                  {(isLearningStyle || isStudySchedule) && (
                                    <p className="text-xs text-muted-foreground">
                                      {isLearningStyle 
                                        ? 'Add your preferred learning style to get personalized content'
                                        : 'Set your optimal study hours for better scheduling'
                                      }
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
